import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import IconCloseSvg from '../../../assets/icons/icon-close.svg?react';
import {
  BaseButton,
  BaseInput,
  PrimaryButton,
  mainHeadingTypographyStyles,
} from '../../../shared/ui';

type RegistrationEmailScreenProps = {
  canSubmit: boolean;
  email: string;
  emailErrorMessage: string | null;
  isEmailInvalid: boolean;
  isSubmitting: boolean;
  onClearEmail: () => void;
  onEmailChange: (email: string) => void;
  onSubmit: () => void;
  submitError: string | null;
};

const Screen = styled.div.attrs({
  'data-ui': 'registration-email-screen',
})`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;

  & > [data-ui='registration-email-form'] {
    margin-bottom: auto;
  }
`;

const Form = styled.section.attrs({
  'data-ui': 'registration-email-form',
})`
  width: ${({ theme }) => theme.scale.px(353)};
  align-self: center;
  display: grid;
  gap: ${({ theme }) => theme.scale.px(22)};
`;

const Heading = styled.div`
  width: ${({ theme }) => theme.scale.px(341)};
  display: grid;
  gap: ${({ theme }) => theme.scale.px(10)};
`;

const Title = styled.h1`
  ${mainHeadingTypographyStyles};
`;

const Subtitle = styled.p`
  width: ${({ theme }) => theme.scale.px(216)};
  margin: 0;
  font-size: ${({ theme }) => theme.scale.px(12)};
  font-weight: 500;
  line-height: 1.2;
`;

const FieldGroup = styled.div.attrs({
  'data-ui': 'registration-email-field',
})`
  display: grid;
  gap: ${({ theme }) => theme.scale.px(6)};
`;

const FieldPanel = styled.div<{ $isInvalid: boolean }>`
  width: ${({ theme }) => theme.scale.px(331)};
  min-height: ${({ theme }) => theme.scale.px(57)};
  align-self: center;
  box-sizing: border-box;
  display: grid;
  grid-template-columns: minmax(0, 1fr) ${({ theme }) => theme.scale.px(14)};
  align-items: center;
  gap: ${({ theme }) => theme.scale.px(12)};
  border: ${({ $isInvalid, theme }) =>
    `${theme.scale.px(1)} solid ${
      $isInvalid ? theme.colors.support.danger : 'transparent'
    }`};
  border-radius: ${({ theme }) => theme.scale.px(10)};
  padding: 0 ${({ theme }) => theme.scale.px(22)} 0
    ${({ theme }) => theme.scale.px(18)};
  background: ${({ theme }) => theme.colors.surface.subtle};
  transition: border-color 160ms ease, box-shadow 160ms ease;

  &:focus-within {
    box-shadow: ${({ $isInvalid, theme }) =>
      $isInvalid
        ? 'none'
        : `0 0 0 ${theme.scale.px(2)} ${theme.colors.brand.primary}`};
  }
`;

const Footer = styled.div.attrs({
  'data-ui': 'registration-email-footer',
})`
  flex-shrink: 0;
  display: grid;
  justify-items: center;
  row-gap: ${({ theme }) => theme.scale.px(14)};
  padding-bottom: ${({ theme }) => theme.scale.px(8)};
`;

const FooterSecondary = styled.div.attrs({
  'data-ui': 'registration-email-footer-secondary',
})`
  width: 100%;
  min-height: ${({ theme }) => theme.scale.px(36)};
  display: grid;
  justify-items: center;
  align-content: start;
`;

const EmailInput = styled(BaseInput)`
  width: 100%;
  min-width: 0;
  height: 100%;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.scale.px(16)};
  font-weight: 400;

  &:focus-visible {
    outline: none;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.border.emphasis};
  }
`;

const ClearAction = styled(BaseButton)`
  width: ${({ theme }) => theme.scale.px(14)};
  height: ${({ theme }) => theme.scale.px(14)};
  display: grid;
  place-items: center;
`;

const ClearIcon = styled(IconCloseSvg).attrs({
  'aria-hidden': true,
  focusable: 'false',
})`
  width: 100%;
  height: 100%;
  display: block;
  color: ${({ theme }) => theme.colors.text.primary};

  & path {
    stroke: currentColor;
  }
`;

const ValidationMessage = styled.p`
  margin: 0;
  text-align: center;
  font-size: ${({ theme }) => theme.scale.px(12)};
  font-weight: 500;
  line-height: 1.2;
  color: ${({ theme }) => theme.colors.support.danger};
  opacity: 0.7;
`;

const SubmitButton = styled(PrimaryButton).attrs({
  'data-ui': 'registration-email-submit',
})<{ $isDisabled: boolean }>`
  align-self: center;
  opacity: ${({ $isDisabled }) => ($isDisabled ? 0.4 : 1)};
  cursor: ${({ $isDisabled }) => ($isDisabled ? 'not-allowed' : 'pointer')};
`;

const SubmitError = styled.p`
  margin: ${({ theme }) => `${theme.scale.px(8)} 0 0`};
  align-self: center;
  text-align: center;
  font-size: ${({ theme }) => theme.scale.px(12)};
  font-weight: 500;
  line-height: 1.2;
  color: ${({ theme }) => theme.colors.support.danger};
`;

export function RegistrationEmailScreen({
  canSubmit,
  email,
  emailErrorMessage,
  isEmailInvalid,
  isSubmitting,
  onClearEmail,
  onEmailChange,
  onSubmit,
  submitError,
}: RegistrationEmailScreenProps) {
  const { t } = useTranslation();
  const isDisabled = !canSubmit || isSubmitting;

  return (
    <Screen>
      <Form>
        <Heading>
          <Title>{t('registrationEmail.title')}</Title>
          <Subtitle>{t('registrationEmail.subtitle')}</Subtitle>
        </Heading>

        <FieldGroup>
          <FieldPanel $isInvalid={isEmailInvalid}>
            <EmailInput
              aria-invalid={isEmailInvalid || undefined}
              aria-label={t('registrationEmail.emailAria')}
              autoComplete="email"
              inputMode="email"
              placeholder={t('registrationEmail.emailPlaceholder')}
              type="email"
              value={email}
              onChange={event => onEmailChange(event.target.value)}
            />
            <ClearAction
              aria-label={t('registrationEmail.clearEmailAria')}
              type="button"
              onClick={onClearEmail}
            >
              <ClearIcon />
            </ClearAction>
          </FieldPanel>
          {emailErrorMessage ? (
            <ValidationMessage>{emailErrorMessage}</ValidationMessage>
          ) : null}
        </FieldGroup>
      </Form>

      <Footer>
        <SubmitButton
          $isDisabled={isDisabled}
          disabled={isDisabled}
          type="button"
          onClick={onSubmit}
        >
          {t('registrationEmail.cta')}
        </SubmitButton>
        <FooterSecondary>
          {submitError ? <SubmitError>{submitError}</SubmitError> : null}
        </FooterSecondary>
      </Footer>
    </Screen>
  );
}
